import React, {
    useEffect,
    useRef,
    useState
} from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { reduxForm, Field, FieldArray, getFormValues } from 'redux-form';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

import ContentCard from '../common/template/contentCard';
import ContentCardBody from '../common/template/contentCardBody';
import ContentCardFooter from '../common/template/contentCardFooter';
import Row from '../common/layout/row';
import Grid from '../common/layout/grid';
import Button from '../common/button/button';
import Form from '../common/form/form';
import LabelAndInput from '../common/form/labelAndInput';
import LabelAndInputDate from '../common/form/labelAndInputDate';
import LabelAndInputMask from '../common/form/labelAndInputMask';
import Select from '../common/form/select';
import ClienteArea from '../clienteArea/clienteArea';

import {
    setModoTela,
    initForm
} from './clienteActions';

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow
});

const POSICAO_PADRAO = {
    lat: -28.3878,
    lng: -53.9198
};

const converterCoordenada = valor => {
    if (
        valor === null ||
        valor === undefined ||
        valor === ''
    ) {
        return null;
    }

    const numero = Number(
        valor
            .toString()
            .trim()
            .replace(',', '.')
    );

    return Number.isFinite(numero)
        ? numero
        : null;
};

const coordenadaEstaNoIntervalo = (valor, minimo, maximo) => {
    const numero = converterCoordenada(valor);

    return numero !== null &&
        numero >= minimo &&
        numero <= maximo;
};

const possuiCoordenadasValidas = (latitude, longitude) =>
    coordenadaEstaNoIntervalo(latitude, -90, 90) &&
    coordenadaEstaNoIntervalo(longitude, -180, 180);

const validarLatitude = valor => {
    if (valor === null || valor === undefined || valor === '') {
        return undefined;
    }

    return coordenadaEstaNoIntervalo(valor, -90, 90)
        ? undefined
        : 'Informe uma latitude entre -90 e 90.';
};

const validarLongitude = valor => {
    if (valor === null || valor === undefined || valor === '') {
        return undefined;
    }

    return coordenadaEstaNoIntervalo(valor, -180, 180)
        ? undefined
        : 'Informe uma longitude entre -180 e 180.';
};

const CampoCoordenada = ({
    input,
    meta,
    label,
    placeholder,
    cols,
    readOnly,
    limite
}) => {
    const possuiErro = meta &&
        meta.touched &&
        meta.error;

    const normalizarAoSair = evento => {
        const valor = evento.target.value;
        const numero = converterCoordenada(valor);

        input.onBlur(
            numero === null
                ? valor
                : numero.toString()
        );
    };

    return (
        <Grid cols={cols}>
            <div className={`form-group${possuiErro ? ' has-error' : ''}`}>
                <label htmlFor={input.name}>
                    {label}
                </label>

                <div className='input-group'>
                    <div className='input-group-prepend'>
                        <span
                            className='input-group-text'
                            title='Coordenada geográfica'
                        >
                            <i className='fas fa-map-marker-alt' />
                        </span>
                    </div>

                    <input
                        {...input}
                        id={input.name}
                        type='text'
                        inputMode='decimal'
                        autoComplete='off'
                        className='form-control'
                        placeholder={placeholder}
                        readOnly={readOnly}
                        onBlur={normalizarAoSair}
                        aria-invalid={possuiErro ? 'true' : 'false'}
                        aria-describedby={`${input.name}-ajuda`}
                    />
                </div>

                <small
                    id={`${input.name}-ajuda`}
                    className={possuiErro ? 'text-danger' : 'text-muted'}
                >
                    {possuiErro
                        ? meta.error
                        : `Graus decimais (${limite})`}
                </small>
            </div>
        </Grid>
    );
};

const obterPosicaoInicial = (latitude, longitude) => {
    const latitudeNumero = converterCoordenada(latitude);
    const longitudeNumero = converterCoordenada(longitude);

    if (possuiCoordenadasValidas(latitude, longitude)) {
        return {
            lat: latitudeNumero,
            lng: longitudeNumero
        };
    }

    return POSICAO_PADRAO;
};

const MapaSelecionarLocalizacao = props => {

    const mapaElementoRef = useRef(null);
    const mapaRef = useRef(null);
    const marcadorRef = useRef(null);

    const [posicao, setPosicao] = useState(
        obterPosicaoInicial(
            props.latitude,
            props.longitude
        )
    );

    const [obtendoLocalizacao, setObtendoLocalizacao] =
        useState(false);

    useEffect(() => {
        if (!props.aberto) {
            return;
        }

        const posicaoInicial = obterPosicaoInicial(
            props.latitude,
            props.longitude
        );

        setPosicao(posicaoInicial);

        const timeout = setTimeout(() => {
            if (!mapaElementoRef.current) {
                return;
            }

            if (mapaRef.current) {
                mapaRef.current.remove();
                mapaRef.current = null;
                marcadorRef.current = null;
            }

            const possuiCoordenadas =
                possuiCoordenadasValidas(
                    props.latitude,
                    props.longitude
                );

            const mapa = L.map(mapaElementoRef.current, {
                center: [posicaoInicial.lat, posicaoInicial.lng],
                zoom: possuiCoordenadas ? 16 : 11
            });

            const mapaNormal = L.tileLayer(
                'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
                {
                    maxZoom: 19,
                    attribution: '&copy; OpenStreetMap'
                }
            );

            const mapaSatelite = L.tileLayer(
                'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                {
                    maxZoom: 18,
                    attribution: 'Tiles © Esri'
                }
            );

            mapaSatelite.addTo(mapa);

            L.control.layers(
                {
                    'Mapa': mapaNormal,
                    'Satélite': mapaSatelite
                },
                null,
                {
                    position: 'topright'
                }
            ).addTo(mapa);

            const marcador = L.marker(
                [
                    posicaoInicial.lat,
                    posicaoInicial.lng
                ],
                {
                    draggable: true
                }
            ).addTo(mapa);

            marcador.on('dragend', evento => {
                const novaPosicao =
                    evento.target.getLatLng();

                setPosicao({
                    lat: novaPosicao.lat,
                    lng: novaPosicao.lng
                });
            });

            mapa.on('click', evento => {
                const novaPosicao = {
                    lat: evento.latlng.lat,
                    lng: evento.latlng.lng
                };

                setPosicao(novaPosicao);

                marcador.setLatLng([
                    novaPosicao.lat,
                    novaPosicao.lng
                ]);
            });

            mapaRef.current = mapa;
            marcadorRef.current = marcador;

            setTimeout(() => {
                mapa.invalidateSize();
            }, 150);
        }, 50);

        return () => {
            clearTimeout(timeout);

            if (mapaRef.current) {
                mapaRef.current.remove();
                mapaRef.current = null;
                marcadorRef.current = null;
            }
        };

    }, [
        props.aberto
    ]);

    useEffect(() => {
        if (!mapaRef.current || !marcadorRef.current) {
            return;
        }

        mapaRef.current.panTo([
            posicao.lat,
            posicao.lng
        ]);

        marcadorRef.current.setLatLng([
            posicao.lat,
            posicao.lng
        ]);
    }, [posicao]);

    useEffect(() => {
        if (!props.aberto) {
            return undefined;
        }

        const fecharComEsc = evento => {
            if (evento.key === 'Escape') {
                props.onFechar();
            }
        };

        document.addEventListener('keydown', fecharComEsc);

        return () =>
            document.removeEventListener('keydown', fecharComEsc);
    }, [props.aberto, props.onFechar]);

    const usarMinhaLocalizacao = () => {
        if (!navigator.geolocation) {
            window.alert(
                'A localização não está disponível neste navegador.'
            );

            return;
        }

        setObtendoLocalizacao(true);

        navigator.geolocation.getCurrentPosition(
            retorno => {
                const novaPosicao = {
                    lat: retorno.coords.latitude,
                    lng: retorno.coords.longitude
                };

                setPosicao(novaPosicao);

                if (mapaRef.current) {
                    mapaRef.current.setView([
                        novaPosicao.lat,
                        novaPosicao.lng
                    ], 17);
                }

                setObtendoLocalizacao(false);
            },
            erro => {
                console.log(erro);

                window.alert(
                    'Não foi possível obter sua localização.'
                );

                setObtendoLocalizacao(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0
            }
        );
    };

    const confirmarLocalizacao = () => {
        props.onConfirmar({
            latitude: posicao.lat.toFixed(7),
            longitude: posicao.lng.toFixed(7)
        });
    };

    if (!props.aberto) {
        return null;
    }

    return (
        <div
            role='dialog'
            aria-modal='true'
            aria-labelledby='titulo-selecionar-localizacao'
            onClick={props.onFechar}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
                background: 'rgba(0, 0, 0, .58)'
            }}
        >
            <div
                onClick={evento => evento.stopPropagation()}
                style={{
                    width: '100%',
                    maxWidth: 1050,
                    maxHeight: '95vh',
                    overflow: 'auto',
                    borderRadius: 16,
                    background: '#fff',
                    boxShadow:
                        '0 18px 50px rgba(0, 0, 0, .28)'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 20px',
                        borderBottom: '1px solid #e5e7eb'
                    }}
                >
                    <div>
                        <div
                            id='titulo-selecionar-localizacao'
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                fontSize: 18,
                                fontWeight: 700,
                                color: '#062042'
                            }}
                        >
                            <i className='fas fa-map-marker-alt'></i>
                            Selecionar localização
                        </div>

                        <div
                            style={{
                                marginTop: 3,
                                fontSize: 13,
                                color: '#6b7280'
                            }}
                        >
                            Clique no ponto exato ou arraste o marcador azul.
                        </div>
                    </div>

                    <button
                        type='button'
                        title='Fechar'
                        onClick={props.onFechar}
                        style={{
                            width: 38,
                            height: 38,
                            border: 'none',
                            borderRadius: 10,
                            background: '#f3f4f6',
                            color: '#374151',
                            cursor: 'pointer'
                        }}
                    >
                        <i className='fas fa-times'></i>
                    </button>
                </div>

                <div style={{ padding: 20 }}>
                    <div
                        ref={mapaElementoRef}
                        aria-label='Mapa para selecionar a localização do cliente'
                        style={{
                            width: '100%',
                            height: 480,
                            borderRadius: 12,
                            overflow: 'hidden',
                            border: '1px solid #d8dde5'
                        }}
                    />

                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 12,
                            marginTop: 16,
                            padding: 14,
                            borderRadius: 10,
                            background: '#f8fafc',
                            border: '1px solid #e5e7eb'
                        }}
                    >
                        <div
                            style={{
                                flex: 1,
                                minWidth: 180
                            }}
                        >
                            <small
                                style={{
                                    color: '#6b7280'
                                }}
                            >
                                Latitude
                            </small>

                            <div
                                style={{
                                    fontWeight: 700,
                                    color: '#062042',
                                    fontFamily: 'monospace'
                                }}
                            >
                                {posicao.lat.toFixed(7)}
                            </div>
                        </div>

                        <div
                            style={{
                                flex: 1,
                                minWidth: 180
                            }}
                        >
                            <small
                                style={{
                                    color: '#6b7280'
                                }}
                            >
                                Longitude
                            </small>

                            <div
                                style={{
                                    fontWeight: 700,
                                    color: '#062042',
                                    fontFamily: 'monospace'
                                }}
                            >
                                {posicao.lng.toFixed(7)}
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        gap: 10,
                        padding: '14px 20px',
                        borderTop: '1px solid #e5e7eb'
                    }}
                >
                    <button
                        type='button'
                        className='btn light'
                        onClick={props.onFechar}
                    >
                        <i
                            className='fas fa-times'
                            style={{ marginRight: 7 }}
                        />
                        Cancelar
                    </button>

                    <button
                        type='button'
                        className='btn primary'
                        disabled={obtendoLocalizacao}
                        onClick={usarMinhaLocalizacao}
                    >
                        <i
                            className='fas fa-crosshairs'
                            style={{ marginRight: 7 }}
                        />

                        {obtendoLocalizacao
                            ? 'Localizando...'
                            : 'Minha localização'}
                    </button>

                    <button
                        type='button'
                        className='btn success'
                        onClick={confirmarLocalizacao}
                    >
                        <i
                            className='fas fa-check'
                            style={{ marginRight: 7 }}
                        />
                        Usar localização
                    </button>
                </div>
            </div>
        </div>
    );
};

let ClienteForm = props => {

    const [mapaAberto, setMapaAberto] = useState(false);

    const formulario = props.formularioValues || {};
    const readOnly = props.excluir ? 'readOnly' : '';
    const localizacaoDefinida = possuiCoordenadasValidas(
        formulario.latitude,
        formulario.longitude
    );

    const confirmarLocalizacao = ({
        latitude,
        longitude
    }) => {
        props.initForm({
            ...formulario,
            latitude,
            longitude
        });

        setMapaAberto(false);
    };

    const limparLocalizacao = () => {
        props.initForm({
            ...formulario,
            latitude: null,
            longitude: null
        });
    };

    const pessoaFisica = formulario.tipo_pessoa === 'fisica';
    const pessoaJuridica = formulario.tipo_pessoa === 'juridica';

    const alterarTipoPessoa = tipoPessoa => {
        props.initForm({
            ...formulario,
            tipo_pessoa: tipoPessoa,
            nome_fantasia: null,
            razao_social: null,
            cnpj: null,
            cpf: null
        });
    };

    return (
        <React.Fragment>
            <ContentCard>
                <Form event={props.handleSubmit}>
                    <ContentCardBody>

                    {!formulario.id &&
                        <Row>
                            <Field
                                name='tipo_pessoa'
                                options={[
                                    { id: 'fisica', valor: 'Pessoa Física' },
                                    { id: 'juridica', valor: 'Pessoa Jurídica' }
                                ]}
                                component={Select}
                                label='Tipo'
                                placeholder='Informe'
                                cols='12 2 2 2'
                                readOnly={readOnly}
                                value={formulario.tipo_pessoa}
                                onChange={alterarTipoPessoa}
                            />
                        </Row>}

                    <Row>
                        {pessoaJuridica &&
                            <Field
                                name='razao_social'
                                component={LabelAndInput}
                                label='Razão Social *'
                                placeholder='Informe'
                                cols='12 5 5 5'
                                readOnly={readOnly}
                            />}

                        {pessoaJuridica &&
                            <Field
                                name='nome_fantasia'
                                component={LabelAndInput}
                                label='Nome Fantasia *'
                                placeholder='Informe'
                                cols='12 5 5 5'
                                readOnly={readOnly}
                            />}

                        {pessoaFisica &&
                            <Field
                                name='razao_social'
                                component={LabelAndInput}
                                label='Nome *'
                                placeholder='Informe'
                                cols='12 5 5 5'
                                readOnly={readOnly}
                            />}
                    </Row>

                    <Row>
                        {pessoaJuridica &&
                            <Field
                                name='cnpj'
                                component={LabelAndInputMask}
                                label='CNPJ'
                                placeholder='Informe'
                                cols='12 1 1 1'
                                minWidth={220}
                                readOnly={readOnly}
                                mask='99.999.999/9999-99'
                            />}

                        {pessoaFisica &&
                            <Field
                                name='cpf'
                                component={LabelAndInputMask}
                                label='CPF'
                                placeholder='Informe'
                                cols='12 1 1 1'
                                minWidth={180}
                                readOnly={readOnly}
                                mask='999.999.999-99'
                            />}

                        <Field
                            name='ie'
                            component={LabelAndInput}
                            label='Inscrição Estadual'
                            placeholder='Informe'
                            cols='12 1 1 1'
                            minWidth={180}
                            readOnly={readOnly}
                        />
                    </Row>

                    <Row>
                        <Field
                            name='endereco'
                            component={LabelAndInput}
                            label='Endereço *'
                            placeholder='Informe'
                            cols='12 4 3 3'
                            readOnly={readOnly}
                        />

                        <Field
                            name='referencia'
                            component={LabelAndInput}
                            label='Referência'
                            placeholder='Informe'
                            cols='12 4 4 4'
                            readOnly={readOnly}
                        />

                        <Field
                            name='cep'
                            component={LabelAndInputMask}
                            label='CEP'
                            placeholder='Informe'
                            cols='12 4 2 2'
                            readOnly={readOnly}
                            mask='99999-999'
                        />
                    </Row>

                    <Row>
                        <Field
                            name='id_cidade'
                            options={props.listaCidade || []}
                            component={Select}
                            label='Cidade *'
                            placeholder='Informe'
                            cols='12 6 5 5'
                            readOnly={readOnly}
                        />
                    </Row>

                    <Row>
                        <Grid cols='12 12 12 12'>
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 8,
                                    marginTop: 4,
                                    marginBottom: 8
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontWeight: 700,
                                            color: '#062042'
                                        }}
                                    >
                                        <i
                                            className='fas fa-map-marked-alt'
                                            style={{ marginRight: 8 }}
                                        />
                                        Localização geográfica
                                    </div>

                                    <small className='text-muted'>
                                        Digite as coordenadas ou marque o ponto exato no mapa.
                                    </small>
                                </div>
                            </div>
                        </Grid>
                    </Row>

                    <Row>
                        <Field
                            name='latitude'
                            component={CampoCoordenada}
                            label='Latitude'
                            placeholder='Ex.: -28.3878000'
                            cols='12 4 3 3'
                            readOnly={readOnly}
                            limite='-90 a 90'
                            validate={validarLatitude}
                        />

                        <Field
                            name='longitude'
                            component={CampoCoordenada}
                            label='Longitude'
                            placeholder='Ex.: -53.9198000'
                            cols='12 4 3 3'
                            readOnly={readOnly}
                            limite='-180 a 180'
                            validate={validarLongitude}
                        />

                        {!readOnly &&
                            <Grid cols='12 4 3 3'>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        gap: 8,
                                        marginTop: 24
                                    }}
                                >
                                    <button
                                        type='button'
                                        title='Abrir o mapa para visualizar ou ajustar a localização'
                                        style={{
                                            flex: 1,
                                            minHeight: 58,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 11,
                                            padding: '8px 12px',
                                            border: '1px solid #0879df',
                                            borderRadius: 11,
                                            background:
                                                'linear-gradient(135deg, #087df1 0%, #075bb5 100%)',
                                            color: '#fff',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            boxShadow:
                                                '0 5px 14px rgba(8, 105, 210, .25)',
                                            transition:
                                                'transform .15s ease, box-shadow .15s ease'
                                        }}
                                        onClick={() => setMapaAberto(true)}
                                        onMouseEnter={evento => {
                                            evento.currentTarget.style.transform =
                                                'translateY(-1px)';
                                            evento.currentTarget.style.boxShadow =
                                                '0 8px 18px rgba(8, 105, 210, .32)';
                                        }}
                                        onMouseLeave={evento => {
                                            evento.currentTarget.style.transform =
                                                'translateY(0)';
                                            evento.currentTarget.style.boxShadow =
                                                '0 5px 14px rgba(8, 105, 210, .25)';
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: 38,
                                                height: 38,
                                                flex: '0 0 38px',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: 10,
                                                background:
                                                    'rgba(255, 255, 255, .16)',
                                                fontSize: 18
                                            }}
                                        >
                                            <i className='fas fa-map-marked-alt' />
                                        </span>

                                        <span
                                            style={{
                                                flex: 1,
                                                minWidth: 0
                                            }}
                                        >
                                            <strong
                                                style={{
                                                    display: 'block',
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                Visualizar no mapa
                                            </strong>

                                            <small
                                                style={{
                                                    display: 'block',
                                                    marginTop: 3,
                                                    color:
                                                        'rgba(255, 255, 255, .82)',
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {localizacaoDefinida
                                                    ? 'Confira ou ajuste o ponto'
                                                    : 'Marque o ponto do cliente'}
                                            </small>
                                        </span>

                                        <i
                                            className='fas fa-chevron-right'
                                            style={{
                                                marginLeft: 2,
                                                opacity: .8,
                                                fontSize: 12
                                            }}
                                        />
                                    </button>

                                    {localizacaoDefinida &&
                                        <button
                                            type='button'
                                            className='btn light'
                                            title='Limpar latitude e longitude'
                                            aria-label='Limpar latitude e longitude'
                                            onClick={limparLocalizacao}
                                            style={{
                                                minWidth: 44,
                                                minHeight: 58,
                                                border: '1px solid #d9e0e8',
                                                borderRadius: 10,
                                                background: '#fff',
                                                color: '#687586'
                                            }}
                                        >
                                            <i className='fas fa-eraser' />
                                        </button>}
                                </div>
                            </Grid>}
                    </Row>

                    <Row>
                        <Select
                            name='ids_consultores'
                            options={props.listaUsuario || []}
                            label='Consultores'
                            cols='12 12 12 12'
                            placeholder='Selecione'
                            readOnly={readOnly}
                            multiple
                            value={formulario.ids_consultores}
                            onChange={values => {
                                props.initForm({
                                    ...formulario,
                                    ids_consultores: (values || []).map(item => item.value)
                                });
                            }}
                        />
                    </Row>

                    <FieldArray
                        name='contatos'
                        component={renderContatos}
                        readOnly={readOnly}
                    />

                    </ContentCardBody>

                    <ContentCardFooter>
                        <Row alignCenter>
                            <Grid cols='6 6 4 3'>
                                <Button
                                    text='Voltar'
                                    type='light'
                                    icon='fa fa-chevron-left'
                                    event={() => props.onVoltar
                                        ? props.onVoltar()
                                        : props.setModoTela('lista')}
                                />
                            </Grid>

                            <Grid cols='6 6 4 3'>
                                {props.excluir &&
                                    <Button
                                        text='Excluir'
                                        submit
                                        type='danger'
                                        icon='fa fa-trash'
                                    />}

                                {!props.excluir &&
                                    <Button
                                        text='Salvar'
                                        submit
                                        type='success'
                                        icon='fa fa-check'
                                    />}
                            </Grid>
                        </Row>
                    </ContentCardFooter>
                </Form>

                <MapaSelecionarLocalizacao
                    aberto={mapaAberto}
                    latitude={formulario.latitude}
                    longitude={formulario.longitude}
                    onFechar={() => setMapaAberto(false)}
                    onConfirmar={confirmarLocalizacao}
                />
            </ContentCard>

            {formulario.id && !props.excluir &&
                <ClienteArea
                    embutido
                    cliente={formulario}
                />}
        </React.Fragment>
    );
};

const renderContatos = ({ fields, readOnly }) => (
    <div style={{ width: '100%' }}>
        {fields.map((contato, index) => (
            <div
                key={index}
                style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 10,
                    marginBottom: 10
                }}
            >
                <div style={{ flex: 1 }}>
                    <Row>
                        <Field
                            name={`${contato}.nome`}
                            component={LabelAndInput}
                            label={index === 0 ? 'Contato *' : 'Contato'}
                            placeholder='Informe'
                            cols='12 4 4 4'
                            maxWidth={350}
                            readOnly={readOnly}
                        />

                        <Field
                            name={`${contato}.celular`}
                            component={LabelAndInputMask}
                            label={index === 0 ? 'Celular *' : 'Celular'}
                            placeholder='Informe'
                            cols='12 3 3 3'
                            maxWidth={180}
                            readOnly={readOnly}
                            mask='(99) 9 9999-9999'
                        />

                        <Field
                            name={`${contato}.email`}
                            component={LabelAndInput}
                            label={index === 0 ? 'E-mail' : 'E-mail'}
                            placeholder='Informe'
                            cols='12 4 4 4'
                            maxWidth={240}
                            readOnly={readOnly}
                        />

                        <Field
                            name={`${contato}.senha`}
                            component={LabelAndInput}
                            label={index === 0 ? 'Senha' : 'Senha'}
                            placeholder='Informe'
                            cols='12 4 4 4'
                            maxWidth={150}
                            readOnly={readOnly}
                        />

                        <Field
                            name={`${contato}.data_nascimento`}
                            component={LabelAndInputDate}
                            label={index === 0 ? 'Data Nascimento' : 'Data Nascimento'}
                            placeholder='Informe'
                            cols='12 4 4 4'
                            maxWidth={240}
                            readOnly={readOnly}
                        />

                        {!readOnly &&
                            <Grid cols='12 1 1 1'>
                                <button
                                    type='button'
                                    title='Remover contato'
                                    onClick={() => fields.remove(index)}
                                    style={{
                                        height: 38,
                                        width: 38,
                                        border: 'none',
                                        borderRadius: 10,
                                        background: '#dc3545',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        marginTop: 29,
                                        boxShadow: '0 2px 6px rgba(220, 53, 69, .35)'
                                    }}
                                >
                                    <i className='fa fa-trash'></i>
                                </button>
                            </Grid>}
                    </Row>
                </div>
            </div>
        ))}

        {!readOnly &&
            <Row>
                <Grid cols='12 3 3 2'>
                    <button
                        type='button'
                        onClick={() => fields.push({
                            id: null,
                            nome: '',
                            celular: '',
                            email: ''
                        })}
                        style={{
                            height: 38,
                            padding: '0 16px',
                            border: 'none',
                            borderRadius: 10,
                            background: '#007bff',
                            color: '#fff',
                            fontWeight: 600,
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(0, 123, 255, .35)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8
                        }}
                    >
                        <i className='fa fa-plus'></i>
                        Adicionar contato
                    </button>
                </Grid>
            </Row>}
    </div>
);

ClienteForm = reduxForm({
    form: 'clienteForm',
    destroyOnUnmount: false
})(ClienteForm);

const mapStateToProps = state => ({
    sessao: state.auth.usuario,
    formularioValues: getFormValues('clienteForm')(state),
    registro: state.cliente.registro,
    listaCidade: state.cidade.listaSelect,
    listaUsuario: state.usuario.listaSelect
});

const mapDispatchToProps = dispatch => bindActionCreators({
    setModoTela,
    initForm
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ClienteForm);
